/**
 * Shared operator-facing vocabulary for the three Tempo delivery modes
 * (GH #2744), hoisted out of CoveragePolicyPage / PoolAppetitePage so the
 * version-history tab renders the SAME words as the pages that publish.
 *
 * The reason this is one module rather than three copies: the retired
 * `enabled` boolean left the examiner-facing Version History tab rendering
 * "Disabled" for every version, including the ones published while the
 * program was scoring the live book. Three enum values do not fit a
 * two-position vocabulary, and a per-file copy of the labels is how the
 * history tab drifted away from the editor in the first place.
 */

import type { TempoDeliveryMode } from '../../hooks/useTempoPolicy';

/**
 * The label a version stamp carries when the field is absent. Versions
 * published before the deliveryMode stamp existed carry no mode, and the spec
 * types the field as required -- so a null here is a PRE-MIGRATION row, not a
 * contract violation. It renders as an evidence gap rather than as a mode:
 * showing "Off" for a stamp nobody wrote is the same defect class as rendering
 * a failed money read as $0 (GH #2742).
 */
export const DELIVERY_MODE_UNRECORDED_LABEL = 'Not recorded';

export const DELIVERY_MODE_LABEL: Record<TempoDeliveryMode, string> = {
  OFF: 'Off',
  PARALLEL_EVALUATION: 'Parallel evaluation',
  PRODUCTION: 'Production',
};

/**
 * The descriptions say what the platform DOES in each mode, because the words
 * alone do not: "parallel evaluation" reads like a preview until you know it
 * scores the live book and records real recommendations.
 */
export const DELIVERY_MODE_DESCRIPTION: Record<TempoDeliveryMode, string> = {
  OFF: 'No scoring runs. Every account records no coverage.',
  PARALLEL_EVALUATION:
    'The scorecard runs against the live book and records real recommendations. No member is offered a limit.',
  PRODUCTION: 'The scorecard runs and the institution acts on the recommended limits.',
};

export const DELIVERY_MODES: readonly TempoDeliveryMode[] = [
  'OFF',
  'PARALLEL_EVALUATION',
  'PRODUCTION',
];

/** Label for a possibly-absent stamp. Never returns an invented mode. */
export function deliveryModeLabel(mode: TempoDeliveryMode | null | undefined): string {
  return mode ? DELIVERY_MODE_LABEL[mode] : DELIVERY_MODE_UNRECORDED_LABEL;
}

/**
 * Badge status for a possibly-absent stamp. All three enum values are TRUTHY,
 * so a boolean ternary over this field renders "enabled" for OFF -- the exact
 * defect this module replaces. An absent stamp is 'warning' (an evidence gap
 * on an examiner surface), never 'inactive' (which reads as a real OFF).
 */
export function deliveryModeBadgeStatus(
  mode: TempoDeliveryMode | null | undefined,
): 'active' | 'inactive' | 'warning' {
  if (!mode) return 'warning';
  return mode === 'OFF' ? 'inactive' : 'active';
}
